import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Ticket, Crown, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const totalDays = last.getDate();
  const weeks = [];
  let day = 1 - startDay;
  while (day <= totalDays) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if (day >= 1 && day <= totalDays) {
        week.push(new Date(year, month, day));
      } else {
        week.push(null);
      }
      day++;
    }
    weeks.push(week);
  }
  return weeks;
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CalendarPage({ user }) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tickets, setTickets] = useState([]);
  const [hostedEvents, setHostedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasses, setShowPasses] = useState(true);
  const [showHosted, setShowHosted] = useState(true);

  const isHost = user?.role === 'host' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const promises = [axios.get(`${API}/users/my-tickets`, { headers })];
      if (isHost) promises.push(axios.get(`${API}/events`, { headers }));
      const results = await Promise.all(promises);
      setTickets(results[0].data);
      if (results[1]) setHostedEvents(results[1].data);
    } catch {
      toast.error('Failed to load calendar data');
    }
    setLoading(false);
  }, [isHost]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const weeks = useMemo(() => getMonthGrid(year, month), [year, month]);
  const today = useMemo(() => dateKey(new Date()), []);

  const eventsByDate = useMemo(() => {
    const map = {};
    if (showPasses) {
      tickets.forEach(t => {
        if (!t.event_date) return;
        const key = t.event_date.split('T')[0];
        if (!map[key]) map[key] = [];
        map[key].push({ type: 'pass', title: t.event_title, id: t.ticket_id, link: `/ticket/${t.ticket_id}`, status: t.status });
      });
    }
    if (showHosted && isHost) {
      hostedEvents.forEach(e => {
        if (!e.date) return;
        const key = e.date.split('T')[0];
        if (!map[key]) map[key] = [];
        map[key].push({ type: 'hosting', title: e.title, id: e.event_id, link: `/events/${e.event_id}/manage` });
      });
    }
    return map;
  }, [tickets, hostedEvents, showPasses, showHosted, isHost]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="calendar-page">
      {/* Header */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => {
              const role = user?.role;
              navigate(role === 'admin' ? '/admin' : role === 'host' ? '/dashboard' : '/my-tickets');
            }} className="text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="calendar-back">
              <ArrowLeft className="w-4 h-4 mr-1" /><span className="text-xs">Dashboard</span>
            </Button>
            <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-lg font-bold text-[#EAE7E1]">Calendar</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPasses(v => !v)} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${showPasses ? 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30' : 'text-[#555] border-[#1A1A1A]'}`} data-testid="toggle-passes">
              <Ticket className="w-3 h-3" /> My Passes
            </button>
            {isHost && (
              <button onClick={() => setShowHosted(v => !v)} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${showHosted ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30' : 'text-[#555] border-[#1A1A1A]'}`} data-testid="toggle-hosted">
                <Crown className="w-3 h-3" /> Hosting
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={prevMonth} className="p-2 text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm transition-colors" data-testid="prev-month">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-[#EAE7E1] tracking-tight" data-testid="month-label">{monthLabel}</h2>
            <button onClick={goToday} className="text-[10px] font-bold uppercase px-2 py-1 text-[#D4AF37] border border-[#D4AF37]/30 rounded-sm hover:bg-[#D4AF37]/10 transition-colors" data-testid="go-today">Today</button>
          </div>
          <button onClick={nextMonth} className="p-2 text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm transition-colors" data-testid="next-month">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-[#1A1A1A] mb-1">
          {DAYS.map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-semibold text-[#777] uppercase tracking-wider">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="border border-[#1A1A1A] rounded-sm overflow-hidden" data-testid="calendar-grid">
          {weeks.map((week, wi) => (
            <div key={`week-${wi}`} className="grid grid-cols-7 divide-x divide-[#1A1A1A] border-b border-[#1A1A1A] last:border-b-0">
              {week.map((day, di) => {
                if (!day) return <div key={`empty-${wi}-${di}`} className="min-h-[80px] sm:min-h-[100px] bg-[#050505]/50" />;
                const key = dateKey(day);
                const isToday = key === today;
                const isPast = key < today;
                const items = eventsByDate[key] || [];
                return (
                  <div key={key} className={`min-h-[80px] sm:min-h-[100px] p-1.5 ${isToday ? 'bg-[#D4AF37]/5' : 'bg-[#0A0A0A]'} ${isPast ? 'opacity-60' : ''}`} data-testid={`day-${key}`}>
                    <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-[#D4AF37]' : 'text-[#777]'}`}>{day.getDate()}</div>
                    <div className="space-y-0.5">
                      {items.slice(0, 3).map(item => (
                        <button
                          key={`${item.type}-${item.id}`}
                          onClick={() => navigate(item.link)}
                          className={`w-full text-left px-1.5 py-0.5 text-[10px] font-medium rounded truncate transition-colors ${
                            item.type === 'hosting'
                              ? 'bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20'
                              : 'bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20'
                          }`}
                          data-testid={`cal-item-${item.id}`}
                        >
                          {item.type === 'hosting' ? '♦ ' : '● '}{item.title}
                        </button>
                      ))}
                      {items.length > 3 && (
                        <div className="text-[9px] text-[#555] pl-1">+{items.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-[#777]">
          {showPasses && <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#16A34A]" /> My Passes</div>}
          {showHosted && isHost && <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Hosting</div>}
        </div>
      </div>
    </div>
  );
}
